import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import useSession from '../admin/useSession';
import { setSession } from '../admin/auth';
import { authApi } from '../services/authApi';

function getInitials(u) {
  if (!u) return '?';
  const p = (u.prenom || '').trim();
  const n = (u.nom || '').trim();
  if (p && n) return (p[0] + n[0]).toUpperCase();
  return (u.email || '?')[0].toUpperCase();
}

const Profil = () => {
  const session = useSession();
  const user  = session?.user;
  const token = session?.token;

  // ⚠ TOUS les hooks doivent être appelés AVANT toute return conditionnelle
  // (React Rules of Hooks — sinon l'ordre des hooks change entre renders).
  const [tab, setTab] = useState('infos'); // 'infos' | 'securite'
  const [infos, setInfos] = useState({
    prenom: user?.prenom || '',
    nom:    user?.nom    || '',
    email:  user?.email  || '',
  });
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [status, setStatus] = useState({ loading: false, error: null, success: null });

  // Garde de route — pas connecté → vers /connexion (APRÈS les hooks)
  if (!session) return <Navigate to="/connexion" replace state={{ from: '/profil' }} />;

  const onChangeInfo = (e) => setInfos((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onChangePwd  = (e) => setPwd((f)   => ({ ...f, [e.target.name]: e.target.value }));

  const dirty = (
    infos.prenom !== user.prenom ||
    infos.nom    !== user.nom    ||
    infos.email  !== user.email
  );

  const submitInfos = async (e) => {
    e.preventDefault();
    if (!dirty) return;
    setStatus({ loading: true, error: null, success: null });
    try {
      const { user: updated } = await authApi.updateMe(token, infos);
      setSession({ token, user: updated });
      setStatus({ loading: false, error: null, success: 'Profil mis à jour.' });
    } catch (err) {
      setStatus({ loading: false, error: err.message, success: null });
    }
  };

  const submitPwd = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, error: null, success: null });
    if (pwd.next.length < 8) return setStatus({ loading: false, error: 'Nouveau mot de passe : 8 caractères minimum.', success: null });
    if (pwd.next !== pwd.confirm) return setStatus({ loading: false, error: 'La confirmation ne correspond pas.', success: null });
    try {
      const { user: updated } = await authApi.updateMe(token, {
        currentPassword: pwd.current,
        newPassword:     pwd.next,
      });
      setSession({ token, user: updated });
      setPwd({ current: '', next: '', confirm: '' });
      setStatus({ loading: false, error: null, success: 'Mot de passe modifié.' });
    } catch (err) {
      setStatus({ loading: false, error: err.message, success: null });
    }
  };

  return (
    <main className="profil-page">
      <div className="wrap profil-wrap">
        <header className="profil-head">
          <span className="eyebrow">Mon espace · {user.role === 'admin' ? 'Admin' : 'Membre'}</span>
          <div className="profil-id">
            <span className="user-av xl">{getInitials(user)}</span>
            <div>
              <h1>{user.fullName || `${user.prenom} ${user.nom}`}</h1>
              <p className="profil-sub">
                {user.email} · membre depuis le{' '}
                {new Date(user.createdAt).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' })}
              </p>
            </div>
          </div>
        </header>

        <div className="profil-tabs">
          <button
            className={`profil-tab${tab === 'infos' ? ' on' : ''}`}
            onClick={() => { setTab('infos'); setStatus({ loading: false, error: null, success: null }); }}
          >
            Informations
          </button>
          <button
            className={`profil-tab${tab === 'securite' ? ' on' : ''}`}
            onClick={() => { setTab('securite'); setStatus({ loading: false, error: null, success: null }); }}
          >
            Sécurité
          </button>
        </div>

        {status.error   && <div className="auth-error">{status.error}</div>}
        {status.success && <div className="auth-success">{status.success}</div>}

        {tab === 'infos' && (
          <form className="profil-form auth-form" onSubmit={submitInfos} noValidate>
            <div className="auth-row">
              <label className="auth-field">
                <span>Prénom</span>
                <input type="text" name="prenom" value={infos.prenom} onChange={onChangeInfo} required />
              </label>
              <label className="auth-field">
                <span>Nom</span>
                <input type="text" name="nom" value={infos.nom} onChange={onChangeInfo} required />
              </label>
            </div>
            <label className="auth-field">
              <span>Email</span>
              <input type="email" name="email" value={infos.email} onChange={onChangeInfo} required />
            </label>
            <button
              type="submit"
              className="btn btn-primary auth-submit"
              disabled={!dirty || status.loading}
            >
              <span>{status.loading ? 'Enregistrement…' : 'Enregistrer'}</span>
              {!status.loading && dirty && <span className="arrow">→</span>}
            </button>
          </form>
        )}

        {tab === 'securite' && (
          <form className="profil-form auth-form" onSubmit={submitPwd} noValidate>
            <label className="auth-field">
              <span>Mot de passe actuel</span>
              <div className="auth-pwd">
                <input
                  type={showPwd ? 'text' : 'password'}
                  name="current"
                  autoComplete="current-password"
                  value={pwd.current}
                  onChange={onChangePwd}
                  required
                />
                <button
                  type="button"
                  className="auth-pwd-toggle"
                  onClick={() => setShowPwd((s) => !s)}
                >
                  {showPwd ? 'Masquer' : 'Afficher'}
                </button>
              </div>
            </label>
            <label className="auth-field">
              <span>Nouveau mot de passe</span>
              <input
                type={showPwd ? 'text' : 'password'}
                name="next"
                autoComplete="new-password"
                placeholder="8 caractères minimum"
                value={pwd.next}
                onChange={onChangePwd}
                minLength={8}
                required
              />
            </label>
            <label className="auth-field">
              <span>Confirmation</span>
              <input
                type={showPwd ? 'text' : 'password'}
                name="confirm"
                autoComplete="new-password"
                value={pwd.confirm}
                onChange={onChangePwd}
                required
              />
            </label>
            <button
              type="submit"
              className="btn btn-primary auth-submit"
              disabled={status.loading || !pwd.current || !pwd.next}
            >
              <span>{status.loading ? 'Mise à jour…' : 'Changer le mot de passe'}</span>
              {!status.loading && <span className="arrow">→</span>}
            </button>
          </form>
        )}
      </div>
    </main>
  );
};

export default Profil;
